import { NotifyReact, NotifyReactEvents } from 'Phaser/EventBus2';
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { OSMDEventStream } from 'Phaser/GameObjects/OSMDEventStream';
import { PlayablePhrase } from 'Phaser/Scenes/SightReadingPhaser';
import Logger from 'Utils/Logger';

enum AccuracyState {
  // Phrase started with accuracy >= 95% and current accuracy >= 95%
  Perfect,
  // Current phrase had segments of accuracy < 95% but current accuracy >= 95%
  NextPerfect,
  // Accuracy < 95% for the current phrase
  Normal,
  // Accuracy < 75% for the current phrase
  Bad,
}

interface CallbackFunction {
  callback: (nextPhrase: PlayablePhrase, activePhrase: PlayablePhrase) => void;
  context?: object;
}

export default class ChevronManager {
  private phrasesTilNextTier = 4;
  private chevronState: AccuracyState = AccuracyState.Normal;
  private eventStream: EventStreamPhaser2 | OSMDEventStream;
  private firstPhrasePlayed = false;
  private onPerfectFinish?: CallbackFunction;
  private onBadFinish?: CallbackFunction;
  private goalTempo: number;
  private tempoEligible: boolean = true;
  private freePlayMode: boolean = false;

  // Customisable phrases till next tier, for use with speeding up/slowing down in repertoire.
  constructor(
    eventStream: EventStreamPhaser2 | OSMDEventStream,
    tempo: number,
    goalTempo: number,
    phrasesTillNextTier: number = 4,
    onGainChevron?: CallbackFunction,
    onLoseChevron?: CallbackFunction,
    freePlayMode: boolean = false,
  ) {
    this.goalTempo = goalTempo;
    this.eventStream = eventStream;
    this.phrasesTilNextTier = phrasesTillNextTier;
    this.onPerfectFinish = onGainChevron;
    this.onBadFinish = onLoseChevron;
    this.freePlayMode = freePlayMode;
  }

  public setGoalTempo(goalTempo: number) {
    this.goalTempo = goalTempo;
  }

  public setFreePlayMode(freePlayMode: boolean) {
    this.freePlayMode = freePlayMode;
    if (freePlayMode !== this.freePlayMode) {
      this.reset();
    }
  }

  public reset(): void {
    this.phrasesTilNextTier = 4;
    this.chevronState = AccuracyState.Normal;
    this.firstPhrasePlayed = false;
    Logger.debug('[ChevronManager] reset to initial state');
  }

  public onPhraseFinishPlaying(
    playedOnce: boolean,
    nextPhrase?: PlayablePhrase,
    activePhrase?: PlayablePhrase,
  ) {
    Logger.debug(
      `[ChevronManager] finishedPlayingPhrase\n
        Chevron State: ${AccuracyState[this.chevronState]}`,
    );
    this.firstPhrasePlayed = true;
    switch (this.chevronState) {
      case AccuracyState.Normal: {
        const acc = this.eventStream.calcAccuracy();
        if (acc >= 95) {
          this.chevronState = AccuracyState.Perfect;
          if (this.tempoEligible) NotifyReactEvents.SetPhraseEligible({ eligible: true });
        }
        break;
      }
      case AccuracyState.Perfect:
        this.perfectFinish(playedOnce, this.tempoEligible, nextPhrase, activePhrase);
        break;
      case AccuracyState.Bad:
        this.badFinish(nextPhrase, activePhrase);
        break;
      case AccuracyState.NextPerfect:
        this.chevronState = AccuracyState.Perfect;
        break;
    }
    // Can be set to eligible only at the end of the phrase, not during the phrase
    // as any dips below the goal tempo will cause the phrase to be ineligible throughout the phrase
    this.tempoEligible = Math.round(this.eventStream.timeKeeper.getBpm()) >= this.goalTempo;
  }

  private perfectFinish(
    playedOnce: boolean,
    tempoEligible: boolean,
    nextPhrase?: PlayablePhrase,
    activePhrase?: PlayablePhrase,
  ) {
    console.debug('[ChevronManager] perfectFinish', { tempoEligible, nextPhrase, activePhrase });
    if (!playedOnce && tempoEligible && !this.freePlayMode) {
      this.phrasesTilNextTier = Math.max(this.phrasesTilNextTier - 1, 0);
      Logger.debug(
        `[ChevronManager] gained chevron\n
        Phrases till next tier: ${this.phrasesTilNextTier}
        ${nextPhrase}
        ${this.onPerfectFinish}`,
      );
      NotifyReactEvents.SetPhrasesTillNextTier({ phrasesTillNextTier: this.phrasesTilNextTier });
    }
    if (nextPhrase && activePhrase)
      this.onPerfectFinish?.callback.call(this.onPerfectFinish?.context, nextPhrase, activePhrase);
  }

  private badFinish(nextPhrase?: PlayablePhrase, activePhrase?: PlayablePhrase) {
    if (!this.freePlayMode) {
      this.phrasesTilNextTier = Math.min(this.phrasesTilNextTier + 1, 4);
      Logger.debug(
        `[ChevronManager] lost chevron\n
        Phrases till next tier: ${this.phrasesTilNextTier}`,
      );
      NotifyReactEvents.SetPhrasesTillNextTier({ phrasesTillNextTier: this.phrasesTilNextTier });
    }
    if (nextPhrase && activePhrase)
      this.onBadFinish?.callback.call(this.onBadFinish?.context, nextPhrase, activePhrase);
  }

  public onTick(tempo: number) {
    Logger.debug(
      `[ChevronManager] onTick\n
        firstPhrasePlayed: ${this.firstPhrasePlayed}`,
    );
    if (!this.firstPhrasePlayed) return;
    const acc = this.eventStream.calcAccuracy();
    if (acc === undefined) {
      Logger.warn('[ChevronManager] Accuracy undefined!');
      return;
    }
    if (acc < 75) {
      this.chevronState = AccuracyState.Bad;
      NotifyReactEvents.SetPhraseEligible({ eligible: false });
    } else if (acc < 95 && this.chevronState != AccuracyState.Normal) {
      this.chevronState = AccuracyState.Normal;
      NotifyReactEvents.SetPhraseEligible({ eligible: false });
    } else if (
      this.chevronState != AccuracyState.Perfect &&
      this.chevronState != AccuracyState.NextPerfect &&
      tempo >= 72
    ) {
      NotifyReactEvents.SetPhraseEligible({ eligible: true });
      this.chevronState = AccuracyState.NextPerfect;
    }
  }
}
